/**
 * Adapter for the proposed v2 operations (docs/forecast-api-v2.md).
 *
 * Always speaks GraphQL. When VUE_APP_FORECAST_V2_MOCK=true the dev-only mock
 * link in src/apollo.js answers these operations from fixtures; otherwise they
 * reach the real backend. A backend that does not know the fields answers with
 * a GraphQL validation error, which this adapter turns into
 * `{ available: false, reason }` so the UI can hide or explain the panels.
 * It never substitutes fixtures for a missing production field.
 */
import gql from "graphql-tag";
import graphqlClient from "../../../apollo";
import { parseBackendJson } from "../lib/parse";

const FORECAST_V2 = gql`
  query forecastToolV2($appJson: String!) {
    forecastToolV2(appJson: $appJson) {
      data
    }
  }
`;

const DIAGNOSTICS = gql`
  query forecastDiagnostics($country: String!) {
    forecastDiagnostics(country: $country) {
      data
    }
  }
`;

function classify(error) {
  const msg = String((error && error.message) || error || "");
  if (/Cannot query field|Unknown argument|Unknown type/i.test(msg)) {
    return { available: false, kind: "unsupported", reason: "The backend does not expose this operation yet (forecast API v2)." };
  }
  if (error && error.networkError) {
    return { available: false, kind: "network", reason: "Network error while loading the forecast evidence.", detail: msg };
  }
  return { available: false, kind: "error", reason: msg || "Unknown error", detail: msg };
}

function unwrap(result, field) {
  const node = result && result.data ? result.data[field] : null;
  if (!node || node.data == null) return { available: false, kind: "missing", reason: `The backend returned no ${field} block.` };
  const doc = parseBackendJson(String(node.data).replaceAll("\\", ""));
  if (!doc) return { available: false, kind: "missing", reason: `The ${field} block could not be parsed.` };
  return { available: true, doc };
}

class ForecastV2Provider {
  async getForecastV2(appJson) {
    try {
      const r = await graphqlClient.query({ query: FORECAST_V2, variables: { appJson }, fetchPolicy: "no-cache" });
      return unwrap(r, "forecastToolV2");
    } catch (e) {
      return classify(e);
    }
  }

  async getDiagnostics(country) {
    try {
      const r = await graphqlClient.query({ query: DIAGNOSTICS, variables: { country }, fetchPolicy: "no-cache" });
      return unwrap(r, "forecastDiagnostics");
    } catch (e) {
      return classify(e);
    }
  }
}

export default new ForecastV2Provider();
